import React, { useState, useEffect } from 'react';
import { auth } from '../../services/firebase.js';
import { getPages, uploadPageImage, getUploadedImages, deletePageImage } from '../../services/companyService.js';
import LoadingSpinner from '../Common/LoadingSpinner.js';
import { doc, updateDoc, collection, addDoc, serverTimestamp, getDocs, deleteDoc, query, where, getDoc } from 'firebase/firestore';
import { db } from '../../services/firebase.js';
import AdminLayout from './AdminLayout.js';
import { layouts } from '../Website/Templates/index.js';
import { generateSEOContent, checkAIUsageLimit, MAX_DAILY_GENERATIONS } from '../../services/ai.js';

const PageEditor = () => {
    const [company, setCompany] = useState(null);
    const [pages, setPages] = useState([]);
    const [selectedPage, setSelectedPage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pageContent, setPageContent] = useState({
        pageName: '',
        title: '',
        subtitle: '',
        content: '',
        image: '',
        hero: {
            backgroundImage: ''
        },
        layout: 'text', // Default layout
        metaTitle: '',
        metaDescription: '',
        gallery: [] // Add this
    });
    const [saving, setSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [uploadedImages, setUploadedImages] = useState([]);
    const [showImageSelector, setShowImageSelector] = useState(false);
    const [imageCount, setImageCount] = useState(0); // Add state for image count
    const [activeTab, setActiveTab] = useState('content'); // Add this
    const [imageSelectType, setImageSelectType] = useState(null); // 'hero' eller 'side'
    const [selectedGalleryIndex, setSelectedGalleryIndex] = useState(null);
    const [keywords, setKeywords] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [currentPage, setCurrentPage] = useState(null); // Add currentPage state

    const user = auth.currentUser;
    //const cvr = user?.email.split('@')[0];

    // useEffect(() => {
    //     const fetchPages = async () => {
    //         try {
    //             const pagesData = await getPages(cvr);
    //             setPages(pagesData);
    //             // Select homepage by default
    //             const homepage = pagesData.find(p => p.type === 'homepage');
    //             if (homepage) setSelectedPage(homepage);
    //         } catch (err) {
    //             setError(err.message);
    //         } finally {
    //             setLoading(false);
    //         }
    //     };

    //     if (cvr) fetchPages();
    // }, [cvr]);

    useEffect(() => {
        const fetchCompany = async () => {
            if (user?.email) {
                try {
                    const companiesRef = collection(db, 'companies');
                    // Use 'email' instead of 'companyEmail'
                    const q = query(companiesRef, where('companyEmail', '==', user.email));
                    const querySnapshot = await getDocs(q);
                    
                    if (!querySnapshot.empty) {
                        const companyData = { 
                            id: querySnapshot.docs[0].id, 
                            ...querySnapshot.docs[0].data() 
                        };
                        setCompany(companyData);
                        
                        // After getting company, fetch pages
                        const pagesData = await getPages(companyData.cvr);
                        setPages(pagesData);
                        
                        // Select homepage by default
                        const homepage = pagesData.find(p => p.type === 'homepage');
                        if (homepage) setSelectedPage(homepage);
                    }
                } catch (err) {
                    console.error('Error fetching company:', err);
                    setError(err.message);
                } finally {
                    setLoading(false);
                }
            }
        };
        fetchCompany();
    }, [user]);

    const cvr = company?.cvr;

    useEffect(() => {
        if (selectedPage) {
            setCurrentPage(selectedPage); // Update useEffect to set currentPage when selectedPage changes
            setPageContent({
                pageName: selectedPage.pageName || selectedPage.title || '',
                title: selectedPage.content.hero?.title || '',
                subtitle: selectedPage.content.hero?.subtitle || '',
                content: selectedPage.content.main?.content || '',
                image: selectedPage.content.main?.image || '',
                hero: {
                    backgroundImage: selectedPage.content.hero?.backgroundImage || ''
                },
                layout: selectedPage.layout || 'text',
                metaTitle: selectedPage.metaTitle || '',
                metaDescription: selectedPage.metaDescription || '',
                gallery: selectedPage.content.main?.gallery || [] // Add this line
            });
        }
    }, [selectedPage]);

    useEffect(() => {
        const fetchImages = async () => {
            if (cvr) {
                try {
                    const images = await getUploadedImages(cvr);
                    setUploadedImages(images);
                    setImageCount(images.length);
                } catch (err) {
                    console.error('Error fetching images:', err);
                }
            }
        };
        
        fetchImages();
    }, [cvr]);

    const handleContentChange = (field, value) => {
        setPageContent(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Update handleImageUpload to handle both hero and side images correctly
    const handleImageUpload = async (e, type) => {
        const file = e.target.files[0];
        if (!file) return;
    
        if (imageCount >= 15) {
            setError('Du kan ikke uploade flere end 15 billeder');
            return;
        }
    
        setSaving(true);
        try {
            const imageUrl = await uploadPageImage(file, cvr);
            
            // Handle different image types
            if (type === 'hero') {
                const newContent = {...pageContent};
                if (!newContent.hero) newContent.hero = {};
                newContent.hero.backgroundImage = imageUrl;
                setPageContent(newContent);
            } else if (type === 'gallery') {
                const newGallery = [...pageContent.gallery];
                newGallery[selectedGalleryIndex] = {
                    ...newGallery[selectedGalleryIndex],
                    image: imageUrl
                };
                handleContentChange('gallery', newGallery);
            } else {
                handleContentChange('image', imageUrl);
            }
            
            setImageCount(prev => prev + 1);
            
            // Refresh image list
            const images = await getUploadedImages(cvr);
            setUploadedImages(images);
        } catch (err) {
            setError('Kunne ikke uploade billede');
        } finally {
            setSaving(false);
        }
    };

    const handleSave = async () => {
        if (!selectedPage || !cvr) return;

        setSaving(true);
        setSaveSuccess(false);
        
        try {
            // Create URL-friendly slug from pageName
            const slug = pageContent.pageName
                .toLowerCase()
                .trim()
                .replace(/[æ]/g, 'ae')
                .replace(/[ø]/g, 'oe')
                .replace(/[å]/g, 'aa')
                .replace(/[^a-z0-9-]/g, '-')
                .replace(/-+/g, '-')
                .replace(/^-+|-+$/g, '');

            const pageRef = doc(db, `companies/${cvr}/pages/${selectedPage.id}`);
            await updateDoc(pageRef, {
                pageName: pageContent.pageName,
                title: pageContent.title,
                slug: slug,
                content: {
                    hero: {
                        title: pageContent.title,
                        subtitle: pageContent.subtitle,
                        backgroundImage: pageContent.hero?.backgroundImage || null // Gem hero baggrundsbillede
                    },
                    main: {
                        content: pageContent.content,
                        image: pageContent.image || null,
                        gallery: pageContent.gallery.map(item => ({
                            title: item.title || '',
                            description: item.description || '',
                            image: item.image || ''
                        }))
                    }
                },
                layout: pageContent.layout,
                metaTitle: pageContent.metaTitle,
                metaDescription: pageContent.metaDescription,
                updatedAt: new Date()
            });

            // Refresh pages list
            const updatedPages = await getPages(cvr);
            setPages(updatedPages);
            
            // Update selected page with new data
            const updatedPage = updatedPages.find(p => p.id === selectedPage.id);
            setSelectedPage(updatedPage);

            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
        } catch (err) {
            setError('Kunne ikke gemme ændringer');
            console.error('Error saving page:', err);
        } finally {
            setSaving(false);
        }
    };

    const handleCreatePage = async () => {
        if (!cvr) return;
        
        setSaving(true);
        try {
            const pagesRef = collection(db, `companies/${cvr}/pages`);
            const companyRef = doc(db, `companies/${cvr}`);

            // Get homepage content
            const pagesSnapshot = await getDocs(pagesRef);
            const homepage = pagesSnapshot.docs.find(doc => doc.data().type === 'homepage');
            
            // Get title and subtitle from homepage
            const homepageData = homepage.data();
            const homeTitle = homepageData.content.hero.title;
            const homeSubtitle = homepageData.content.hero.subtitle;

            // Create new page
            const newPage = {
                pageName: 'Ny side',
                title: 'Ny side',
                slug: 'ny-side',
                type: 'page',
                layout: 'text',
                content: {
                    hero: {
                        title: 'Ny side',
                        subtitle: ''
                    },
                    main: {
                        content: 'Indtast din tekst her...',
                        image: null
                    }
                },
                metaTitle: '',
                metaDescription: '',
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            };

            // First create the page
            const docRef = await addDoc(pagesRef, newPage);

            // Then update the company document with homepage content
            await updateDoc(companyRef, {
                'websiteContent.title': homeTitle,
                'websiteContent.subtitle': homeSubtitle,
                'websiteContent.processedContent': {
                    title: homeTitle,
                    subtitle: homeSubtitle
                }
            });

            const newPageWithId = { 
                id: docRef.id, 
                ...newPage,
                createdAt: new Date()
            };
            
            setPages(prev => [...prev, newPageWithId]);
            setSelectedPage(newPageWithId);
        } catch (err) {
            setError('Kunne ikke oprette ny side');
            console.error('Error creating page:', err);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedPage || !cvr || selectedPage.type === 'homepage') return;

        if (!window.confirm('Er du sikker på at du vil slette denne side? Dette kan ikke fortrydes.')) {
            return;
        }

        setSaving(true);
        try {
            const pageRef = doc(db, `companies/${cvr}/pages/${selectedPage.id}`);
            await deleteDoc(pageRef);
            
            // Update local state
            setPages(prev => prev.filter(p => p.id !== selectedPage.id));
            setSelectedPage(null);
        } catch (err) {
            setError('Kunne ikke slette siden');
            console.error('Error deleting page:', err);
        } finally {
            setSaving(false);
        }
    };

    const getDateValue = (date) => {
        if (!date) return 0;
        if (date instanceof Date) return date.getTime();
        if (typeof date === 'string') return new Date(date).getTime();
        if (date.toDate && typeof date.toDate === 'function') return date.toDate().getTime();
        return 0;
    };

    const handleDeleteImage = async (image) => {
        if (!window.confirm('Er du sikker på at du vil slette dette billede? Dette kan ikke fortrydes.')) {
            return;
        }
    
        setSaving(true);
        try {
            await deletePageImage(image.path, cvr);
            setImageCount(prev => prev - 1);
            
            // Refresh image list
            const images = await getUploadedImages(cvr);
            setUploadedImages(images);
    
            // Clear image from current page if it's the one being deleted
            if (pageContent.image === image.url) {
                handleContentChange('image', '');
            }
        } catch (err) {
            setError('Kunne ikke slette billedet');
        } finally {
            setSaving(false);
        }
    };
    
    // Update ImageSelector component
    const ImageSelector = () => (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium font-montserrat">
                        Vælg {imageSelectType === 'hero' ? 'hero baggrundsbillede' : 'sidebillede'}
                    </h3>
                    <button 
                        onClick={() => {
                            setShowImageSelector(false);
                            setImageSelectType(null);
                        }}
                        className="text-gray-400 hover:text-gray-500"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <div className="grid grid-cols-3 gap-4">
                    {uploadedImages.map((image) => (
                        <div key={image.path} className="relative group">
                            <button
                                onClick={() => {
                                    if (imageSelectType === 'hero') {
                                        const newContent = {...pageContent};
                                        if (!newContent.hero) newContent.hero = {};
                                        newContent.hero.backgroundImage = image.url;
                                        setPageContent(newContent);
                                    } else if (imageSelectType === 'gallery') {
                                        const newGallery = [...pageContent.gallery];
                                        newGallery[selectedGalleryIndex] = {
                                            ...newGallery[selectedGalleryIndex],
                                            image: image.url
                                        };
                                        handleContentChange('gallery', newGallery);
                                    } else {
                                        handleContentChange('image', image.url);
                                    }
                                    setShowImageSelector(false);
                                    setImageSelectType(null);
                                }}
                                className="w-full aspect-square border rounded-lg overflow-hidden hover:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500"
                            >
                                <img 
                                    src={image.url} 
                                    alt={image.name}
                                    className="w-full h-full object-cover"
                                />
                            </button>
                            <button
                                onClick={() => handleDeleteImage(image)}
                                className="absolute top-2 right-2 p-1 bg-red-600 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Slet billede"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    const tabs = [
        { 
            id: 'content', 
            name: 'Indhold',
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
            )
        },
        ...(pageContent.layout !== 'gallery' ? [{ 
            id: 'media', 
            name: 'Billeder',
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
            )
        }] : []),
        { 
            id: 'layout', 
            name: 'Layout',
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6z" />
                </svg>
            )
        },
        { 
            id: 'seo', 
            name: 'SEO',
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
            )
        }
    ];

    const renderTabContent = () => {
        switch (activeTab) {
            case 'content':
                return (
                    <div className="bg-white rounded-lg p-6">
                        <div className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    Sidenavn (Kun synligt i CMS)
                                </label>
                                <input
                                    type="text"
                                    value={pageContent.pageName}
                                    onChange={(e) => handleContentChange('pageName', e.target.value)}
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                                />
                            </div>

                            {/* Vis kun titel og indhold hvis det IKKE er gallery layout */}
                            {pageContent.layout !== 'gallery' && (
                                <>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            Overskrift
                                        </label>
                                        <input
                                            type="text"
                                            value={pageContent.title}
                                            onChange={(e) => handleContentChange('title', e.target.value)}
                                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                                        />
                                    </div>

                                    {selectedPage?.layout === 'hero' && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">
                                                Underoverskrift
                                            </label>
                                            <input
                                                type="text"
                                                value={pageContent.subtitle}
                                                onChange={(e) => handleContentChange('subtitle', e.target.value)}
                                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                                            />
                                        </div>
                                    )}

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            Indhold
                                        </label>
                                        <textarea
                                            value={pageContent.content}
                                            onChange={(e) => handleContentChange('content', e.target.value)}
                                            rows={10}
                                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                                        />
                                        <p className="mt-1 text-xs text-gray-500">
                                            Tip: Brug to linjeskift for at lave et nyt afsnit.<br/>
                                            Du kan tilføje links ved at skrive [link tekst](url), f.eks. [Kontakt os](/kontakt)
                                        </p>
                                    </div>

                                    {/* AI Text Generator */}
                                    <div className="mt-6 bg-gray-50 p-6 rounded-lg">
                                        <div className="flex justify-between items-center mb-4">
                                            <h3 className="text-lg font-semibold">AI Tekstgenerator</h3>
                                            <span className="text-sm text-gray-600">
                                                {company.aiUsage?.count || 0}/{MAX_DAILY_GENERATIONS} i dag
                                            </span>
                                        </div>
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Nøgleord (adskilt med komma)
                                                </label>
                                                <input
                                                    type="text"
                                                    value={keywords}
                                                    onChange={(e) => setKeywords(e.target.value)}
                                                    placeholder="f.eks.: kvalitet, erfaring, professionel"
                                                    className="w-full p-2 border rounded-md"
                                                />
                                            </div>
                                            <button
                                                onClick={handleGenerateContent}
                                                disabled={isGenerating || !keywords.trim()}
                                                className="bg-brand-dark text-white px-4 py-2 rounded-md hover:bg-brand-dark/90 disabled:opacity-50"
                                            >
                                                {isGenerating ? (
                                                    <span className="flex items-center">
                                                        <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                        </svg>
                                                        Genererer...
                                                    </span>
                                                ) : (
                                                    'Generer tekst med AI'
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                        
                        {selectedPage?.layout === 'gallery' && (
                            <div className="mt-6">
                                <h3 className="text-lg font-medium mb-4">Galleri</h3>
                                <div className="space-y-4">
                                    {pageContent.gallery?.map((item, index) => (
                                        <div key={index} className="border rounded-lg p-4">
                                            <div className="flex justify-between mb-4">
                                                <h4 className="font-medium">Billede {index + 1}</h4>
                                                <button
                                                    onClick={() => {
                                                        const newGallery = [...pageContent.gallery];
                                                        newGallery.splice(index, 1);
                                                        handleContentChange('gallery', newGallery);
                                                    }}
                                                    className="text-red-600"
                                                >
                                                    Slet
                                                </button>
                                            </div>
                                            <div className="space-y-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">
                                                        Titel
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={item.title || ''}
                                                        onChange={(e) => {
                                                            const newGallery = [...pageContent.gallery];
                                                            newGallery[index] = {
                                                                ...newGallery[index],
                                                                title: e.target.value
                                                            };
                                                            handleContentChange('gallery', newGallery);
                                                        }}
                                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">
                                                        Beskrivelse
                                                    </label>
                                                    <textarea
                                                        value={item.description || ''}
                                                        onChange={(e) => {
                                                            const newGallery = [...pageContent.gallery];
                                                            newGallery[index] = {
                                                                ...newGallery[index],
                                                                description: e.target.value
                                                            };
                                                            handleContentChange('gallery', newGallery);
                                                        }}
                                                        rows={3}
                                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700">
                                                        Billede
                                                    </label>
                                                    {item.image ? (
                                                        <div className="relative mt-2">
                                                            <img 
                                                                src={item.image} 
                                                                alt="" 
                                                                className="w-full h-48 object-cover rounded-lg"
                                                            />
                                                            <button
                                                                onClick={() => {
                                                                    const newGallery = [...pageContent.gallery];
                                                                    newGallery[index] = {
                                                                        ...newGallery[index],
                                                                        image: ''
                                                                    };
                                                                    handleContentChange('gallery', newGallery);
                                                                }}
                                                                className="absolute top-2 right-2 p-1 bg-red-600 rounded-full text-white"
                                                            >
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                                                </svg>
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            onClick={() => {
                                                                setImageSelectType('gallery');
                                                                setSelectedGalleryIndex(index);
                                                                setShowImageSelector(true);
                                                            }}
                                                            className="mt-2 px-4 py-2 border border-gray-300 rounded-md"
                                                        >
                                                            Vælg billede
                                                        </button>
                                                    )}
                                                    <div class="text-sm text-gray-500 mt-1">Du kan uploade billeder i Billedearkiv</div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    <button
                                        onClick={() => {
                                            const newGallery = [...(pageContent.gallery || []), {}];
                                            handleContentChange('gallery', newGallery);
                                        }}
                                        className="mt-4 px-4 py-2 bg-brand-light/20 text-brand-dark rounded-md"
                                    >
                                        Tilføj billede
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                );
            case 'media':
                return (
                    <div className="bg-white rounded-lg p-6">
                        <h3 className="text-lg font-medium font-montserrat text-gray-900 mb-4">Billeder</h3>
                        <div className="space-y-6">
                            {/* Hero background image - only show for hero layout */}
                            {selectedPage?.layout === 'hero' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Hero baggrundsbillede
                                    </label>
                                    <div className="space-y-4">
                                        {pageContent.hero?.backgroundImage ? (
                                            <div className="relative">
                                                <img 
                                                    src={pageContent.hero.backgroundImage}
                                                    alt="Hero background"
                                                    className="w-full h-48 object-cover rounded-lg"
                                                />
                                                <button
                                                    onClick={() => {
                                                        const newContent = {...pageContent};
                                                        if (!newContent.hero) newContent.hero = {};
                                                        newContent.hero.backgroundImage = '';
                                                        setPageContent(newContent);
                                                    }}
                                                    className="absolute top-2 right-2 p-1 bg-red-600 rounded-full text-white hover:bg-red-700"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="flex items-center space-x-4">
                                                <button
                                                    onClick={() => {
                                                        setImageSelectType('hero');
                                                        setShowImageSelector(true);
                                                    }}
                                                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                                                >
                                                    Vælg baggrundsbillede
                                                </button>
                                                {imageCount < 15 && (
                                                    <>
                                                        <span className="text-gray-500">eller</span>
                                                        <input
                                                            type="file"
                                                            accept="image/*"
                                                            onChange={(e) => {
                                                                setImageSelectType('hero');
                                                                handleImageUpload(e, 'hero');
                                                            }}
                                                            className="block text-sm text-gray-500
                                                                file:mr-4 file:py-2 file:px-4
                                                                file:rounded-md file:border-0
                                                                file:text-sm file:font-semibold
                                                                file:bg-brand-light/20 file:text-brand-dark
                                                                hover:file:bg-brand-light/30"
                                                        />
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="border-t pt-6">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Sidebillede
                                </label>
                                <div className="space-y-4">
                                    {pageContent.image ? (
                                        <div className="relative">
                                            <img 
                                                src={pageContent.image}
                                                alt=""  // Empty alt since it's decorative
                                                className="w-full h-48 object-cover rounded-lg"
                                            />
                                            <button
                                                onClick={() => handleContentChange('image', '')}
                                                className="absolute top-2 right-2 p-1 bg-red-600 rounded-full text-white hover:bg-red-700"
                                            >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex items-center space-x-4">
                                            <button
                                                onClick={() => {
                                                    setImageSelectType('side');
                                                    setShowImageSelector(true);
                                                }}
                                                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                                            >
                                                Vælg sidebillede
                                            </button>
                                            {imageCount < 15 && (
                                                <>
                                                    <span className="text-gray-500">eller</span>
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={(e) => {
                                                            setImageSelectType('side');
                                                            handleImageUpload(e, 'side');
                                                        }}
                                                        className="block text-sm text-gray-500
                                                            file:mr-4 file:py-2 file:px-4
                                                            file:rounded-md file:border-0
                                                            file:text-sm file:font-semibold
                                                            file:bg-brand-light/20 file:text-brand-dark
                                                            hover:file:bg-brand-light/30"
                                                    />
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                );
            case 'layout':
                return (
                    <div className="bg-white rounded-lg p-6">
                        <h3 className="text-lg font-medium font-montserrat text-gray-900 mb-4">Side layout</h3>
                        <div className="grid grid-cols-2 gap-4">
                            {/* ... existing layout options ... */}
                            {layouts.map(layout => (
                                <label 
                                    key={layout.id}
                                    className={`relative flex flex-col p-4 border-2 rounded-lg cursor-pointer
                                        ${pageContent.layout === layout.id 
                                            ? 'border-brand-dark bg-brand-light/20' 
                                            : 'border-gray-200 hover:border-gray-300'}`}
                                >
                                    <input
                                        type="radio"
                                        name="layout"
                                        value={layout.id}
                                        checked={pageContent.layout === layout.id}
                                        onChange={(e) => handleContentChange('layout', e.target.value)}
                                        className="sr-only"
                                    />
                                    {layout.preview}
                                    <div className="mt-4">
                                        <div className="font-medium text-gray-900">{layout.name}</div>
                                        <div className="text-sm text-gray-500">{layout.description}</div>
                                    </div>
                                    {pageContent.layout === layout.id && (
                                        <div className="absolute -top-2 -right-2 w-6 h-6 bg-brand-dark rounded-full flex items-center justify-center">
                                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                    )}
                                </label>
                            ))}
                        </div>
                    </div>
                );
            case 'seo':
                return (
                    <div className="bg-white rounded-lg p-6">
                        <h3 className="text-lg font-medium font-montserrat text-gray-900 mb-4">SEO & Metadata</h3>
                        <div className="space-y-4">
                            {/* ... existing SEO fields ... */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    Meta titel
                                </label>
                                <input
                                    type="text"
                                    value={pageContent.metaTitle}
                                    onChange={(e) => handleContentChange('metaTitle', e.target.value)}
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                                    maxLength={60}
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                    Maksimum 60 tegn - Dette vises i søgeresultater
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    Meta beskrivelse
                                </label>
                                <textarea
                                    value={pageContent.metaDescription}
                                    onChange={(e) => handleContentChange('metaDescription', e.target.value)}
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                                    maxLength={160}
                                    rows={3}
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                    Maksimum 160 tegn - Dette vises i søgeresultater
                                </p>
                            </div>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    const handleGenerateContent = async () => {
        setIsGenerating(true);
        try {
            // Check usage limit before generating
            await checkAIUsageLimit(company.cvr);

            const content = await generateSEOContent({
                companyName: company.name,
                industryDesc: company.industryDesc,
                keywords: keywords.split(',').map(k => k.trim()),
                pageType: currentPage?.type || 'page'
            });

            // Get updated company data to reflect new AI usage count
            const companyRef = doc(db, 'companies', company.cvr);
            const companyDoc = await getDoc(companyRef);
            const updatedCompany = { id: companyDoc.id, ...companyDoc.data() };
            setCompany(updatedCompany);

            // Add new text under existing content
            const existingContent = pageContent.content.trim();
            const newContent = existingContent 
                ? `${existingContent}\n\n--- AI Genereret indhold ---\n\n${content.content}`
                : content.content;

            handleContentChange('content', newContent);
            if (!pageContent.title.trim()) {
                handleContentChange('title', content.title);
            }
        } catch (error) {
            console.error('Failed to generate content:', error);
            setError(error.message || 'Kunne ikke generere indhold');
        } finally {
            setIsGenerating(false);
        }
    };

    if (loading) return <LoadingSpinner />;

    return (
        <AdminLayout>
            <div className="bg-gray-100 grid grid-cols-1 md:flex md:space-x-4 min-h-[calc(100vh-4rem)]">
                {/* Left sidebar */}
                <div className="w-64 bg-white shadow-sm">
                    <div className="p-4 border-b">
                        <h2 className="text-lg font-medium font-montserrat text-gray-900">Sider</h2>
                    </div>
                    <nav className="p-2">
                        {pages
                            .sort((a, b) => getDateValue(a.createdAt) - getDateValue(b.createdAt))
                            .map(page => (
                                <button
                                    key={page.id}
                                    type="button"
                                    onClick={() => setSelectedPage(page)}
                                    className={`w-full text-left p-3 rounded-md mb-1 transition-all duration-200 ${
                                        selectedPage?.id === page.id 
                                            ? 'bg-brand-light/20 shadow-sm' 
                                            : 'hover:bg-gray-50'
                                    }`}
                                >
                                    <div className="flex items-center">
                                        <span className="flex-1">{page.pageName || page.title}</span>
                                        {selectedPage?.id === page.id && (
                                            <svg className="w-5 h-5 text-brand-dark" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                        )}
                                    </div>
                                </button>
                            ))}
                    </nav>
                    <div className="p-4 border-t">
                        <button
                            onClick={handleCreatePage}
                            className="w-full text-left p-2 rounded-md bg-brand-light/20 hover:bg-brand-light/30 flex items-center"
                        >
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                            </svg>
                            Opret ny side
                        </button>
                    </div>
                </div>

                {/* Main content */}
                <div className="flex-1 pb-8">
                    {selectedPage ? (
                        <div className="space-y-6">
                            <div className="flex justify-between items-center">
                                <h1 className="text-2xl font-bold font-montserrat text-gray-900">
                                    {selectedPage.pageName}
                                </h1>
                                <div className="flex items-center space-x-4">
                                    {saveSuccess && (
                                        <span className="text-green-600 text-sm">
                                            Ændringer gemt!
                                        </span>
                                    )}
                                    {selectedPage.type !== 'homepage' && (
                                        <button
                                            onClick={handleDelete}
                                            disabled={saving}
                                            className="text-red-600 hover:text-red-700 px-4 py-2 rounded-md disabled:opacity-50"
                                        >
                                            {saving ? 'Sletter...' : 'Slet side'}
                                        </button>
                                    )}
                                    <button
                                        onClick={handleSave}
                                        disabled={saving}
                                        className="bg-brand-dark hover:bg-brand-dark/90 text-white px-4 py-2 rounded-md disabled:opacity-50"
                                    >
                                        {saving ? 'Gemmer...' : 'Gem ændringer'}
                                    </button>
                                </div>
                            </div>

                            {/* Tabs */}
                            <div className="border-b border-gray-200 mt-6">
                                <nav className="-mb-px grid grid-cols-2 md:flex space-x-8">
                                    {tabs.map((tab) => (
                                        <button
                                            key={tab.id}
                                            onClick={() => setActiveTab(tab.id)}
                                            className={`
                                                flex items-center py-4 px-1 border-b-2 font-medium text-sm
                                                ${activeTab === tab.id
                                                    ? 'border-brand-dark text-brand-dark'
                                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                                }
                                            `}
                                        >
                                            {tab.icon}
                                            <span className="ml-2">{tab.name}</span>
                                        </button>
                                    ))}
                                </nav>
                            </div>

                            {/* Tab content */}
                            <div className="mt-6">
                                {renderTabContent()}
                            </div>

                            {error && (
                                <div className="text-red-600 text-sm mt-2">
                                    {error}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-center text-gray-500">
                            Vælg en side fra menuen
                        </div>
                    )}
                </div>
            </div>
            {showImageSelector && (
                <ImageSelector />
            )}
        </AdminLayout>
    );
};

export default PageEditor;